import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  MenuItem,
  IconButton,
  Menu,
  MenuItem as MenuOption,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  Link as MuiLink,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import useVendorStore from "../../store/useVendorStore";
import { EVIDENCE_TYPES, RECOMMENDATIONS, SECURITY_REVIEW_STATUS } from "../../utils/constants";
import { SECURITY_QUESTIONNAIRE_TEMPLATE } from "../../data/vendorSeedData";
import {
  enrichSoc2File,
  findSoc2File,
  getKnownVendorReport,
} from "../../utils/knownVendorReports";
import { styles } from "../../styles/Vendors.styles";
import StatusChip from "../../components/StatusChip";

const SUMMARY_LIMIT = 10000;

const RECOMMENDATION_OPTIONS = [
  {
    value: RECOMMENDATIONS.APPROVED,
    title: "Approved",
    description: "The assessment is approved. You can optionally specify a scope of use below.",
    icon: CheckCircleOutlineIcon,
  },
  {
    value: RECOMMENDATIONS.CONDITIONALLY_APPROVED,
    title: "Conditionally approved",
    description: "Approved only if specific follow-up conditions are completed. Be sure to add details below.",
    icon: ListAltIcon,
  },
  {
    value: RECOMMENDATIONS.NOT_APPROVED,
    title: "Not approved",
    description: "This request is rejected, and the assessment can't be used.",
    icon: HighlightOffIcon,
  },
];

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "-";

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const inferEvidenceType = (fileName = "") => {
  const name = fileName.toLowerCase();
  if (name.includes("soc 2") || name.includes("soc2")) return "SOC 2 Type II";
  if (name.includes("iso") && name.includes("27001")) return "ISO 27001";
  if (name.includes("pentest") || name.includes("penetration")) return "Penetration Test";
  if (name.includes("dpa")) return "DPA";
  if (name.includes("policy")) return "Security Policy";
  return "SOC 2 Type II";
};

const localFileUrls = new Map();

const rememberLocalFile = (id, file) => {
  const existing = localFileUrls.get(id);
  if (existing) URL.revokeObjectURL(existing);
  const url = URL.createObjectURL(file);
  localFileUrls.set(id, url);
  return url;
};

const TrustCenterCard = ({ vendor, trustUrl }) => {
  const theme = useTheme();
  if (!trustUrl) return null;

  const label = trustUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: 2,
        mb: 3,
        borderRadius: "10px",
        border: `1px solid ${theme.customColors.border.main}`,
        backgroundColor: theme.customColors.background.infoBox,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }}>
        <PolicyOutlinedIcon sx={{ mt: 0.25, color: theme.customColors.icons.primary }} />
        <Box>
          <Typography variant="body2" fontWeight={600} color={theme.customColors.text.strong}>
            Trust Center
          </Typography>
          <Typography variant="caption" color={theme.customColors.text.secondary}>
            Public security documentation for {vendor.name}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <MuiLink href={trustUrl} target="_blank" rel="noopener noreferrer" fontWeight={600}>
              {label}
            </MuiLink>
          </Typography>
        </Box>
      </Box>
      <Button
        variant="outlined"
        size="small"
        endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
        href={trustUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          textTransform: "none",
          borderRadius: "8px",
          borderColor: theme.customColors.border.main,
          color: theme.customColors.text.primary,
        }}
      >
        Open Trust Center
      </Button>
    </Box>
  );
};

const RequirementsCard = ({ onUpload }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: 2,
        mb: 3,
        borderRadius: "10px",
        border: `1px solid ${theme.customColors.border.main}`,
        backgroundColor: theme.customColors.background.header,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <InfoOutlinedIcon sx={{ mt: 0.25, color: theme.customColors.icons.primary }} />
        <Box>
          <Typography variant="body2" fontWeight={600} color={theme.customColors.text.strong}>
            Requirements
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Upload a security assessment (e.g. SOC 2 Type II) before recording a recommendation.
          </Typography>
        </Box>
      </Box>
      <Button
        variant="contained"
        startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
        onClick={onUpload}
        sx={styles.addButton}
      >
        Upload documents
      </Button>
    </Box>
  );
};

const Soc2ReportDialog = ({ open, onClose, report, vendor }) => {
  const theme = useTheme();
  if (!report) return null;
  const trustUrl = report.trustCenterUrl || vendor.trustCenterUrl;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "16px",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        SOC 2 Type II Report
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            p: 2.5,
            borderRadius: "10px",
            border: `1px solid ${theme.customColors.border.main}`,
            backgroundColor: theme.customColors.background.header,
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} color={theme.customColors.text.strong}>
            {vendor.name}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Service Organization Control Report relevant to Security, Availability, and Confidentiality
          </Typography>
        </Box>
        <Stack spacing={1.25}>
          <Typography variant="body2"><strong>Document:</strong> {report.name}</Typography>
          <Typography variant="body2"><strong>Type:</strong> {report.type || "SOC 2 Type II"}</Typography>
          {report.auditor && (
            <Typography variant="body2"><strong>Independent auditor:</strong> {report.auditor}</Typography>
          )}
          {report.period && (
            <Typography variant="body2"><strong>Period:</strong> {report.period}</Typography>
          )}
          {report.opinion && (
            <Typography variant="body2"><strong>Opinion:</strong> {report.opinion}</Typography>
          )}
          <Typography variant="body2"><strong>Status:</strong> {report.status || "Valid"}</Typography>
          <Typography variant="body2"><strong>Source:</strong> {report.source || "Trust Center"}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, background: theme.customColors.background.cardHeader }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: theme.customColors.text.secondary }}>
          Close
        </Button>
        {trustUrl && (
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            href={trustUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={styles.addButton}
          >
            Open Trust Center
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const SecurityReviewForm = ({
  vendor,
  review,
  readOnly,
  onComplete,
  onDiscard,
  onSnackbar,
}) => {
  const theme = useTheme();
  const updateReview = useVendorStore((s) => s.updateReview);
  const addReviewFile = useVendorStore((s) => s.addReviewFile);
  const removeReviewFile = useVendorStore((s) => s.removeReviewFile);
  const fileInputRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pickedFiles, setPickedFiles] = useState([]);
  const [form, setForm] = useState({ name: "", type: "SOC 2 Type II", source: "Vendor" });

  const isDraft = review.status === SECURITY_REVIEW_STATUS.DRAFT;
  const knownReport = getKnownVendorReport(vendor);
  const known = Boolean(knownReport);
  const trustUrl = vendor.trustCenterUrl || knownReport?.trustCenterUrl;
  const files = review.files || [];
  const hasAssessment = files.length > 0;
  const needsUpload = isDraft && !readOnly && !hasAssessment && !known;
  const soc2 = enrichSoc2File(findSoc2File(files), vendor);
  const canComplete = isDraft && !!review.recommendation && hasAssessment && !readOnly;
  const heading =
    review.status === SECURITY_REVIEW_STATUS.COMPLETED
      ? "Assessment completed"
      : review.status === SECURITY_REVIEW_STATUS.DISCARDED
        ? "Assessment discarded"
        : "Assessment in progress";

  const handleRecommendation = (value) => {
    if (readOnly || needsUpload) return;
    updateReview(vendor.id, review.id, { recommendation: value });
  };

  const handleSummary = (value) => {
    if (readOnly || needsUpload) return;
    updateReview(vendor.id, review.id, { summary: value.slice(0, SUMMARY_LIMIT) });
  };

  const resetUploadDialog = () => {
    setPickedFiles([]);
    setForm({ name: "", type: "SOC 2 Type II", source: "Vendor" });
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const closeUploadDialog = () => {
    setUploadOpen(false);
    resetUploadDialog();
  };

  const applyPickedFiles = (fileList) => {
    const next = Array.from(fileList || []);
    if (!next.length) return;
    setPickedFiles(next);
    const first = next[0];
    setForm((prev) => ({
      ...prev,
      name: first.name.replace(/\.[^.]+$/, ""),
      type: inferEvidenceType(first.name),
    }));
  };

  const handleUpload = () => {
    if (!pickedFiles.length) return;
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    pickedFiles.forEach((picked) => {
      const id = `e-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      rememberLocalFile(id, picked);
      const displayName =
        pickedFiles.length === 1 && form.name.trim()
          ? form.name.trim().includes(".")
            ? form.name.trim()
            : `${form.name.trim()}${picked.name.includes(".") ? picked.name.slice(picked.name.lastIndexOf(".")) : ""}`
          : picked.name;
      addReviewFile(vendor.id, review.id, {
        id,
        name: displayName,
        type: form.type,
        status: "Valid",
        source: form.source,
        sizeBytes: picked.size,
        mimeType: picked.type || "application/octet-stream",
        localUpload: true,
        expiresAt: expires.toISOString(),
      });
    });
    closeUploadDialog();
    onSnackbar?.(
      pickedFiles.length === 1 ? "File uploaded" : `${pickedFiles.length} files uploaded`
    );
  };

  const handleViewFile = (file) => {
    const localUrl = localFileUrls.get(file.id);
    if (localUrl) {
      window.open(localUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (findSoc2File([file])) {
      setReportOpen(true);
      return;
    }
    onSnackbar?.("Preview is available for files uploaded in this session");
  };

  const handleRemoveFile = (file) => {
    const url = localFileUrls.get(file.id);
    if (url) {
      URL.revokeObjectURL(url);
      localFileUrls.delete(file.id);
    }
    removeReviewFile(vendor.id, review.id, file.id);
  };

  const handleQuestionnaire = () => {
    onSnackbar?.(`Questionnaire request sent to ${vendor.contactName || vendor.name}`);
  };

  const completeHint = !hasAssessment
    ? "Upload a security assessment to complete this review"
    : "Select a recommendation to complete this assessment";

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} color={theme.customColors.text.strong}>
            {heading}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {known && hasAssessment
              ? "SOC 2 report retrieved from the vendor Trust Center."
              : "Upload a security assessment (e.g. SOC 2 report) for audit documentation."}
          </Typography>
        </Box>
        {isDraft && !readOnly && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={canComplete ? "" : completeHint} arrow>
              <span>
                <Button
                  variant="contained"
                  disabled={!canComplete}
                  onClick={onComplete}
                  sx={styles.addButton}
                >
                  Mark assessment as complete
                </Button>
              </span>
            </Tooltip>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                border: `1px solid ${theme.customColors.border.main}`,
                borderRadius: "8px",
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuOption
                onClick={() => {
                  setMenuAnchor(null);
                  onDiscard?.();
                }}
                sx={{ color: theme.customColors.status.error.text }}
              >
                Discard assessment
              </MenuOption>
            </Menu>
          </Stack>
        )}
      </Box>

      <TrustCenterCard vendor={vendor} trustUrl={trustUrl} />

      {needsUpload && <RequirementsCard onUpload={() => setUploadOpen(true)} />}

      {hasAssessment && (
        <>
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary, mb: 1, fontWeight: 600 }}
          >
            Recommendation
          </Typography>
          <Stack spacing={1.25} sx={{ mb: 3 }}>
            {RECOMMENDATION_OPTIONS.map((option) => {
              const selected = review.recommendation === option.value;
              const Icon = option.icon;
              return (
                <Box
                  key={option.value}
                  onClick={() => handleRecommendation(option.value)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: 1.75,
                    borderRadius: "10px",
                    cursor: readOnly ? "default" : "pointer",
                    border: `1px solid ${
                      selected
                        ? theme.customColors.button.primary
                        : theme.customColors.border.main
                    }`,
                    backgroundColor: selected
                      ? theme.customColors.background.info
                      : theme.customColors.background.main,
                    "&:hover": readOnly
                      ? undefined
                      : { borderColor: theme.customColors.button.primary },
                  }}
                >
                  <Icon
                    sx={{
                      mt: 0.25,
                      color: selected
                        ? theme.customColors.button.primary
                        : theme.customColors.text.secondary,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={theme.customColors.text.strong}
                    >
                      {option.title}
                    </Typography>
                    <Typography variant="caption" color={theme.customColors.text.secondary}>
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </>
      )}

      <Typography
        variant="body2"
        sx={{ color: theme.customColors.text.secondary, mb: 1, fontWeight: 600 }}
      >
        Files
      </Typography>
      {!readOnly && !needsUpload && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setUploadOpen(true)}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.primary,
            }}
          >
            Upload files
          </Button>
          <Button
            variant="outlined"
            onClick={handleQuestionnaire}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.primary,
            }}
          >
            Generate questionnaire request
          </Button>
          <Button
            variant="outlined"
            onClick={() => setTemplatesOpen(true)}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.primary,
            }}
          >
            View templates
          </Button>
        </Stack>
      )}
      {!hasAssessment ? (
        <Typography variant="body2" sx={{ mb: 3, color: theme.customColors.text.secondary }}>
          {needsUpload
            ? "No security assessment on file yet. Upload a SOC 2 report to continue."
            : "No files uploaded yet"}
        </Typography>
      ) : (
        <Box sx={{ mb: 3 }}>
          {files.map((file, idx) => {
            const isSoc2 = findSoc2File([file]);
            const preview = isSoc2 ? enrichSoc2File(file, vendor) : file;
            const canPreview = Boolean(localFileUrls.get(file.id) || isSoc2);
            return (
              <React.Fragment key={file.id || file.name || idx}>
                {idx > 0 && <Divider sx={{ borderColor: theme.customColors.border.main }} />}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, flexWrap: "wrap" }}>
                  <DescriptionOutlinedIcon sx={{ color: theme.customColors.icons.secondary }} />
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography variant="body2" fontWeight={600} color={theme.customColors.text.strong}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption">
                      {file.type} · {file.source}
                      {file.sizeBytes ? ` · ${formatBytes(file.sizeBytes)}` : ""}
                      {preview?.auditor ? ` · ${preview.auditor}` : ""}
                      {preview?.period ? ` · ${preview.period}` : ` · ${formatDate(file.uploadedAt)}`}
                    </Typography>
                  </Box>
                  <StatusChip label={file.status} />
                  {canPreview && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewFile(file)}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        borderColor: theme.customColors.border.main,
                        color: theme.customColors.text.primary,
                      }}
                    >
                      View
                    </Button>
                  )}
                  {isDraft && !readOnly && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(file)}
                      sx={{ color: theme.customColors.text.secondary }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      )}

      {hasAssessment && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: theme.customColors.text.secondary, fontWeight: 600 }}
            >
              Summary of findings
            </Typography>
            <Tooltip title="Record residual risk notes, exceptions, and follow-up conditions." arrow>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: theme.customColors.text.secondary }} />
            </Tooltip>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={5}
            placeholder="Enter..."
            value={review.summary || ""}
            onChange={(e) => handleSummary(e.target.value)}
            disabled={readOnly}
            inputProps={{ maxLength: SUMMARY_LIMIT }}
          />
          <Typography variant="caption" sx={{ display: "block", mt: 0.75 }}>
            {(review.summary || "").length}/{SUMMARY_LIMIT} characters
          </Typography>
        </>
      )}

      <Soc2ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        report={soc2}
        vendor={vendor}
      />

      <Dialog open={uploadOpen} onClose={closeUploadDialog} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "16px",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
          }}
        >
          Upload files
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xlsx,.csv"
              onChange={(e) => applyPickedFiles(e.target.files)}
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                applyPickedFiles(e.dataTransfer.files);
              }}
              sx={{
                p: 2.5,
                borderRadius: "10px",
                border: `1px dashed ${
                  dragOver ? theme.customColors.button.primary : theme.customColors.border.main
                }`,
                backgroundColor: dragOver
                  ? theme.customColors.background.info
                  : theme.customColors.background.header,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <FileUploadOutlinedIcon sx={{ color: theme.customColors.icons.primary, mb: 0.5 }} />
              <Typography variant="body2" fontWeight={600} color={theme.customColors.text.strong}>
                Drop files here or click to browse
              </Typography>
              <Typography variant="caption">
                PDF, Word, or image files. Nothing is sent to a server.
              </Typography>
            </Box>

            {pickedFiles.length > 0 && (
              <Stack spacing={1}>
                {pickedFiles.map((picked) => (
                  <Box
                    key={`${picked.name}-${picked.size}-${picked.lastModified}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: "8px",
                      border: `1px solid ${theme.customColors.border.main}`,
                    }}
                  >
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: theme.customColors.icons.secondary }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {picked.name}
                      </Typography>
                      <Typography variant="caption">{formatBytes(picked.size)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}

            <TextField
              label="Document name"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Document name"
              InputLabelProps={{ shrink: true }}
              disabled={pickedFiles.length > 1}
            />
            <TextField
              select
              label="Type"
              size="small"
              fullWidth
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            >
              {EVIDENCE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Source"
              size="small"
              fullWidth
              value={form.source}
              onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            >
              {["Vendor", "Trust Center", "Internal"].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: theme.customColors.background.cardHeader }}>
          <Button
            onClick={closeUploadDialog}
            sx={{ textTransform: "none", color: theme.customColors.text.secondary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!pickedFiles.length}
            sx={styles.addButton}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templatesOpen} onClose={() => setTemplatesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "16px",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
          }}
        >
          Questionnaire templates
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Standard security questionnaire sent to vendors.
          </Typography>
          {SECURITY_QUESTIONNAIRE_TEMPLATE.map((q, idx) => (
            <Typography key={q.key} variant="body2" sx={{ mb: 1 }}>
              {idx + 1}. {q.question}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: theme.customColors.background.cardHeader }}>
          <Button
            onClick={() => setTemplatesOpen(false)}
            sx={{ textTransform: "none", color: theme.customColors.text.secondary }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityReviewForm;
